import { CheckIcon, TriangleAlertIcon } from 'lucide-react'

import { useComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import { useQuotingEmployeeContext } from 'components/quoting/employees/QuotingEmployeeContext'
import { type QuoteResult } from 'components/quoting/types'
import { AllowanceStrategies, type AllowanceStrategy } from 'pages/employer_onboarding/quote/types'
import { CollapsibleContainer } from 'slab'
import { calculateAgeAtDate, cn, formatMoney } from 'utils'
import { calculateEmployeeMonthlyAllowanceForEmployeeAndDependents, formatEmployeeContributionPercentage } from 'utils/quote_calculations'

import { useQuote } from './QuoteProvider'

export const ChecklistCard = (
  {
    isLoading,
    quoteResults,
    allowanceType = AllowanceStrategies.Smart,
    employerEmployeeContribution = 0,
    employerSpouseContribution = 0,
    employerChildContribution = 0,
    calculatedCostsByEmployeeId,
  } : {
    isLoading: boolean,
    quoteResults: QuoteResult[],
    allowanceType?: AllowanceStrategy,
    employerEmployeeContribution?: number,
    employerSpouseContribution?: number,
    employerChildContribution?: number,
    calculatedCostsByEmployeeId?: Record<string, { employeeAllowanceAmount: number }>,
  }
) => {
  const {
    selectedEmployee,
    onEmployeeSelected,
  } = useQuotingEmployeeContext()

  const { quote } = useQuote()

  const {
    employeesWithUnaffordablePlans,
    youngestAndOldestEmployeesByRatingArea,
    ratingAreasWithUnfairAllowances,
    numPassingChecks,
  } = useComplianceCalculations(
    quoteResults,
    allowanceType,
    employerEmployeeContribution,
    calculatedCostsByEmployeeId
  )

  return (
    <div className='bg-white border border-gray-200 rounded-2xl p-4 shadow-md'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xs text-gray-500'>Checklist</h2>
        {isLoading
          ? <div className='w-12 h-4 bg-gray-200 animate-pulse rounded-md' />
          : (
            <div className='flex items-center gap-2'>
              <span className='text-xs text-gray-500'>{numPassingChecks}/2 checks passed</span>
            </div>
            )}
      </div>

      {isLoading
        ? (
          <div className='w-full h-44 bg-gray-200 animate-pulse rounded-md' />
          )
        : (
          <div className='flex flex-col gap-2'>
            <CollapsibleContainer
              heading={employeesWithUnaffordablePlans.length > 0 ? `${employeesWithUnaffordablePlans.length} employee${employeesWithUnaffordablePlans.length === 1 ? ' has' : 's have'} unaffordable coverage` : 'All employees have affordable coverage'}
              headingIconContent={employeesWithUnaffordablePlans.length > 0 ? <TriangleAlertIcon className='w-4 h-4 text-red-400' /> : <CheckIcon className='w-4 h-4 text-green-400' />}
              defaultOpen={false}
            >
              <div className='flex flex-col gap-2 text-xs text-gray-700 p-2'>
                <p>In order for an allowance to be considered affordable, employees must be able to purchase the lowest cost silver plan without paying more than {formatEmployeeContributionPercentage(quote?.max_employee_contribution_percentage)}% of their annual income.</p>
                {employeesWithUnaffordablePlans.length > 0 && (
                  <ol className='list-decimal list-inside max-h-56 overflow-y-auto my-2'>
                    {employeesWithUnaffordablePlans.map((result) => (
                      <li
                        key={result.id}
                        className={cn('cursor-pointer hover:underline', selectedEmployee?.id === result.id && 'font-semibold')}
                        onClick={() => onEmployeeSelected(result)}
                      >
                        {result.name}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </CollapsibleContainer>
            <CollapsibleContainer
              heading={ratingAreasWithUnfairAllowances.length > 0 ? `${ratingAreasWithUnfairAllowances.length} rating area${ratingAreasWithUnfairAllowances.length === 1 ? ' has' : 's have'} an unfair allowance structure` : 'Fair allowance rule'}
              headingIconContent={ratingAreasWithUnfairAllowances.length > 0 ? <TriangleAlertIcon className='w-4 h-4 text-red-400' /> : <CheckIcon className='w-4 h-4 text-green-400' />}
              defaultOpen={false}
            >
              <div className='text-xs text-gray-700 p-2'>
                {ratingAreasWithUnfairAllowances.length > 0
                  ? <p>The oldest employee's allowance is more than 3x the youngest employee's allowance in {ratingAreasWithUnfairAllowances.length} rating areas.</p>
                  : <p>Your coverage is considered fair because your oldest employee's allowance is not more than 3x the youngest employee's allowance in all {Object.keys(youngestAndOldestEmployeesByRatingArea).length} rating areas.</p>}
                {ratingAreasWithUnfairAllowances.length > 0 && (
                  ratingAreasWithUnfairAllowances.map(([ratingArea, employees]) => (
                    <div key={ratingArea} className='flex gap-4 mt-2'>
                      <div className='flex-grow'>
                        <span
                          className={cn('block cursor-pointer hover:underline', selectedEmployee?.id === employees.oldest.id && 'font-semibold')}
                          onClick={() => onEmployeeSelected(employees.oldest)}
                        >
                          {employees.oldest.name} ({calculateAgeAtDate(employees.oldest.date_of_birth, new Date())} years old)
                        </span>
                        <span
                          className={cn('block cursor-pointer hover:underline', selectedEmployee?.id === employees.youngest.id && 'font-semibold')}
                          onClick={() => onEmployeeSelected(employees.youngest)}
                        >
                          {employees.youngest.name} ({calculateAgeAtDate(employees.youngest.date_of_birth, new Date())} years old)
                        </span>
                      </div>
                      <div>
                        <p className='font-mono'>
                          {formatMoney(
                            calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(employees.oldest, {
                              employerEmployeeContribution,
                              employerSpouseContribution,
                              employerChildContribution
                            })
                          )}
                        </p>
                        <p className='font-mono'>
                          {formatMoney(
                            calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(employees.youngest, {
                              employerEmployeeContribution,
                              employerSpouseContribution,
                              employerChildContribution
                            })
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CollapsibleContainer>
          </div>
          )}
    </div>
  )
}

export default ChecklistCard
